// Send a stack of drawings to someone, in one act.
//
// Sharing was built one sheet at a time, which is right for the case it
// was built for - "here is the drawing you asked about" - and wrong for
// the case people actually have: a subcontractor needs the twelve
// sheets that make up their package. Twelve modals, twelve invitations,
// twelve emails to the same person.
//
// This is the SAME action repeated, deliberately: it posts to the same
// /shares/sheet/:id/invite as the single-sheet modal, once per sheet, so
// a bulk share and twelve single shares produce identical access. There
// is no bulk endpoint and no bulk share object, because a "share of many
// sheets" would be a second kind of grant to reason about, revoke, and
// get wrong - and the recipient's view of it would be the same list
// either way.
//
// What it does NOT do is pretend the twelve succeeded when nine did. The
// per-sheet result is reported per sheet.
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Modal, Select, Input, Tag, message } from "antd";
import { url, tokenRef } from "api";
import { getParticipantsRoster } from "api/participants/participantsApi";
import { T } from "pages/Issue/theme";
import { filterPeopleOption, renderPeopleOption } from "components/PeoplePicker/PeoplePicker";
import {
  peoplePickerOptions, flatOptions, emailValue, companyPickValue, expandCompanyPicks, companyOptionLabelWithCount,
} from "components/PeoplePicker/peoplePickerOptions";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const auth = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem(tokenRef)}`,
});

const labelOf = (s) => `${s.sheetNumber ? `${s.sheetNumber} — ` : ""}${s.sheetName || "Sheet"}`;

export default function ShareManySheetsModal({ sheets = [], projectId, open, onClose, onDone }) {
  const [roster, setRoster] = useState([]);
  const [picked, setPicked] = useState([]);
  const [guestName, setGuestName] = useState("");
  const [busy, setBusy] = useState(false);
  const [failures, setFailures] = useState([]);

  useEffect(() => {
    if (!open) { setPicked([]); setGuestName(""); setFailures([]); }
  }, [open]);

  useEffect(() => {
    if (!open || !projectId) return;
    getParticipantsRoster(projectId)
      .then((g) => setRoster(Array.isArray(g) ? g : []))
      .catch(() => setRoster([]));
  }, [open, projectId]);

  // The same picker as the single-sheet dialog (components/PeoplePicker):
  // partners under their trade, a "Whole company" row expanded to its
  // people's addresses on the spot.
  const options = useMemo(() => peoplePickerOptions(roster, {
    personValue: emailValue,
    companyValue: companyPickValue,
    companyOptionLabel: companyOptionLabelWithCount,
  }), [roster]);

  const knownEmails = useMemo(
    () => new Set(flatOptions(options).filter((o) => o.person).map((o) => o.value)),
    [options]
  );
  const strangers = picked.filter((e) => !knownEmails.has(e));

  // Every selected sheet must belong to one project, because the roster
  // that decides who can be invited is a PROJECT's roster. A mixed
  // selection would offer people who have no standing on half of it.
  const projects = useMemo(
    () => [...new Set(sheets.map((s) => String(s?.projectId?._id || s?.projectId || "")))].filter(Boolean),
    [sheets]
  );
  const mixed = projects.length > 1;

  const submit = async () => {
    const bad = picked.find((e) => !EMAIL_RE.test(e));
    if (bad) return message.warning(`"${bad}" is not an email address`);
    if (!picked.length) return message.warning("Pick someone, or type an email address");
    if (mixed) return message.warning("Select sheets from a single project to share them together");

    const people = picked.map((email) => ({
      email,
      name: (strangers.length === 1 && strangers[0] === email) ? guestName.trim() : "",
    }));

    setBusy(true);
    setFailures([]);
    const failed = [];
    let ok = 0;
    // Serially, not in parallel: each of these creates a share and sends
    // mail, and firing twelve at a box with two cores mostly produces
    // twelve slower requests and a rate-limited mailer.
    for (const s of sheets) {
      try {
        const r = await fetch(`${url}/shares/sheet/${s._id}/invite`, {
          method: "POST", headers: auth(), body: JSON.stringify({ people }),
        });
        const b = await r.json();
        if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
        ok += 1;
      } catch (e) {
        failed.push({ sheet: labelOf(s), err: e.message || "failed" });
      }
    }
    setBusy(false);
    setFailures(failed);

    if (!failed.length) {
      message.success(`${picked.length} ${picked.length === 1 ? "person" : "people"} given access to ${ok} sheet${ok === 1 ? "" : "s"}`);
      if (onDone) onDone();
      if (onClose) onClose();
    } else {
      // Left open on purpose, with the list of what did not go.
      message.warning(`${ok} shared, ${failed.length} could not be`);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText={`Share ${sheets.length} sheet${sheets.length === 1 ? "" : "s"}`}
      okButtonProps={{ disabled: !sheets.length || mixed }}
      confirmLoading={busy}
      width={620}
      title="Share the selected sheets"
      destroyOnClose
    >
      {mixed && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message="These sheets are from more than one project"
          description="Who may be invited is decided by a project's roster, so a selection has to sit in one project. Narrow the selection and share again."
        />
      )}

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 6 }}>
          {sheets.length} sheet{sheets.length === 1 ? "" : "s"} selected
        </div>
        <div style={{ maxHeight: 132, overflow: "auto", border: `1px solid ${T.border || "#eee"}`, borderRadius: 4, padding: 8 }}>
          {sheets.map((s) => <Tag key={s._id} style={{ marginBottom: 4 }}>{labelOf(s)}</Tag>)}
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>Share with</div>
        <Select
          mode="tags"
          style={{ width: "100%" }}
          value={picked}
          onChange={(v) => setPicked(expandCompanyPicks(v, roster).map((e) => String(e).toLowerCase()))}
          options={options}
          filterOption={filterPeopleOption}
          optionRender={renderPeopleOption}
          placeholder="Pick from the project, or type an email address"
          maxTagCount={6}
          tokenSeparators={[",", " ", ";"]}
        />
      </div>

      {strangers.length === 1 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>
            Their name (optional) — {strangers[0]} has no account on this project
          </div>
          <Input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Name" />
        </div>
      )}

      {!!failures.length && (
        <Alert
          type="error" showIcon style={{ marginTop: 12 }}
          message={`${failures.length} sheet${failures.length === 1 ? "" : "s"} could not be shared`}
          description={
            <div style={{ maxHeight: 120, overflow: "auto" }}>
              {failures.map((f, i) => <div key={i} style={{ fontSize: 12 }}>{f.sheet} — {f.err}</div>)}
            </div>
          }
        />
      )}

      <div style={{ fontSize: 12, color: T.muted, marginTop: 12 }}>
        Each sheet is shared read-only, exactly as sharing one sheet at a time would.
        Manage or revoke an individual sheet from its own share button.
      </div>
    </Modal>
  );
}
